import { FC, useEffect, useRef, useState } from "react";

// Images
import homeLocationA from "../../../assets/img/HomePage/Mobile/home_location_a.jpg";
import homeLocationB from "../../../assets/img/HomePage/Mobile/home_location_b.jpg";
import homeLocationC from "../../../assets/img/HomePage/Mobile/home_location_c.jpg";

import { ReactComponent as LocationIconFilled } from "../../../icons/LocationIconFilled.svg";

const HomeLocations = () => {
  return (
    <section className="pl-[20px] py-[40px] bg-[#F8F8F8]">
      <div className="bg-text-1000 pl-[50px] py-[40px] rounded-l-[16px] shadow-md shadow-text-0/24">
        {/* Heading */}
        <div className="mb-[36px]">
          <h4 className="text-[24px] font-raleway font-bold text-primaryDark-0 leading-tight">
            <span className="text-primaryDark-400"> Available </span> Locations
          </h4>

          <p className="text-[14px] font-open-sans text-text-600 mt-[8px]">Swipe to see more locations</p>
        </div>

        {/* Image Grid */}
        <HomeLocationsImageGrid />
      </div>
    </section>
  );
};

const HomeLocationsImageGrid = () => {
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const startX = useRef(0);
  const currentX = useRef(0);
  const isDragging = useRef(false);

  const totalSlides = 3;

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setIndex((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused]);

  // TOUCH START
  const handleStart = (x: number) => {
    startX.current = x;
    isDragging.current = true;
    setIsPaused(true);
  };

  // TOUCH MOVE
  const handleMove = (x: number) => {
    if (!isDragging.current) return;
    currentX.current = x;
  };

  // TOUCH END
  const handleEnd = () => {
    if (!isDragging.current) return;

    const diff = startX.current - currentX.current;

    if (diff > 50) {
      setIndex((prev) => (prev === totalSlides - 1 ? 0 : prev + 1)); // swipe left → next
    } else if (diff < -50) {
      setIndex((prev) => (prev === 0 ? totalSlides - 1 : prev - 1)); // swipe right → prev
    }

    isDragging.current = false;
    setIsPaused(false);
  };

  return (
    <div
      className="w-full overflow-hidden relative"
      onTouchStart={(e) => handleStart(e.touches[0].clientX)}
      onTouchMove={(e) => handleMove(e.touches[0].clientX)}
      onTouchEnd={handleEnd}
    >
      <div className="grid grid-cols-1 w-full">
        <div
          className="flex transition-transform duration-500"
          style={{
            transform: `translateX(-${index * 100}%)`,
          }}
        >
          <HomeLocationsImageGridItem
            imageSrc={homeLocationA}
            imgLocation="Washington D.C."
          />
          <HomeLocationsImageGridItem
            imageSrc={homeLocationB}
            imgLocation="Maryland"
          />
          <HomeLocationsImageGridItem
            imageSrc={homeLocationC}
            imgLocation="Northern Virginia"
          />
        </div>
      </div>
    </div>
  );
};

type HomeLocationsImageGridItemProps = {
  imageSrc: string;
  imgLocation: string;
};

const HomeLocationsImageGridItem: FC<HomeLocationsImageGridItemProps> = ({
  imageSrc,
  imgLocation,
}) => {
  return (
    <div className="w-full bg-[#F8F8F8] cursor-pointer relative flex-shrink-0">
      {/* Image Box */}
      <img
        src={imageSrc}
        alt="location"
        className="w-full h-full object-cover rounded-l-[8px]"
      />

      {/* Location Box */}
      <div className="absolute z-10 bottom-[16px] right-[16px] p-[8px] bg-text-1000/20 rounded-[32px]">
        <div className="px-[20px] py-[10px] flex items-center gap-[8px] bg-text-1000 rounded-[32px]">
          <LocationIconFilled className="text-primaryDark-400 w-[20px] h-[20px]" />
          <p className="text-[14px] font-raleway font-bold text-text-0">
            {imgLocation}
          </p>
        </div>
      </div>
    </div>
  );
};

export default HomeLocations;
